import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './add-to-cart.module.scss';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { Button, Incrementer, Checkbox, Icon } from '@pella/sash-framework';
import { getSessionStorage, removeSessionStorage, setCookie, getCookie } from '../../../../utilities/functions';
import { setTag, getCurrentScope, setContext, captureMessage } from '@sentry/gatsby';
import { useCart } from '../../../../../hooks/use-cart';
import { useProject } from '../../../../../hooks/use-project';
import { useProduct } from '../../../../../hooks/use-product';

const AddToCart = (props) => {
	const CSData = useStaticQuery(query).contentstackSummaryFields;
	const [editItem, setEditItem] = useState(props.editItem || null);
	const [wasEdited, setWasEdited] = useState(false);
	const [bundleData, setBundleData] = useState({});
	const [quantity, setQuantity] = useState(props.itemQty);
	const [addInstallKit, setAddInstallKit] = useState(false);
	const { addItemToCart, updateCartItem, addBundleToCart } = useCart();
	const { updateProjectItem } = useProject();
	const { updateConfigQuantity, getBundle } = useProduct();

	useEffect(() => {
		if (getSessionStorage('userModified') || props.userModification) {
			setWasEdited(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	useEffect(() => {
		if (props.showInstallKit && props.bundleID) {
			const fetchBundle = async (id) => {
				const bundle = await getBundle(id);
				if (bundle?.data?.bundle_items?.[0]) {
					setBundleData(bundle.data);
				} else {
					if (bundle?.data?.error) {
						setTag('Pella Component', 'AddToCart', 'API Transaction ID', bundle?.REQUEST_ID);
						getCurrentScope().setTransactionName('getBundle');
						setContext('Error', {
							errorData: JSON.stringify(bundle?.data) || 'Unknown Error Data',
							responseURL: bundle?.request?.responseURL,
							configData: bundle?.config?.data,
						});
						captureMessage(bundle.data?.error?.message || 'Unknown Error Details');
					}
					return null;
				}
			};

			fetchBundle(props.bundleID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- need to fix later, outside of current scope
	}, [props.showInstallKit, props.isPro, props.bundleID, props.productType]);

	useEffect(() => {
		if (wasEdited !== props.userModification) {
			setWasEdited(true);
		}
		if (editItem !== props.editItem) {
			setEditItem(props.editItem);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [props?.editItem, props?.userModification]);

	useEffect(() => {
		if (props?.currentItem?.quantity) {
			setQuantity(props.itemQty >= props.currentItem.quantity ? props.itemQty : props.currentItem.quantity);
		}
	}, [props?.currentItem?.quantity, props?.itemQty]);

	const [origQty, setOrigQty] = useState(quantity);
	const updateQuantity = (qty) => {
		if (qty > 0) {
			props.updateLoadingStatus(true);
			setQuantity(qty);
			const updatedQty = props.updateQuantity(qty);
			if (updatedQty) {
				setOrigQty(quantity);
				props.updateLoadingStatus(false);
			} else {
				setQuantity(origQty);
				props.updateLoadingStatus(false);
			}
		}
	};

	const saveToCart = async () => {
		if (props.currentItem?.available_quantity ? props.currentItem?.available_quantity >= quantity : quantity) {
			if (props.bundleID && props.isBundlePDP) {
				await saveBundleToCart(props.bundleID, quantity);
			} else {
				editItem ? (wasEdited || props.itemEdited ? saveUpdatedProductToCart(editItem.itemID, quantity) : navigate('/shop/cart/')) : saveProductToCart(quantity);
			}
		}
	};

	const saveProductToCart = async (quantity) => {
		props.updateLoadingStatus(true);
		const bundleID = props.bundleID && addInstallKit ? { bundleID: props.bundleID, quantity: quantity } : null;

		try {
			const updateQuantity = await updateConfigQuantity(quantity, props.configKey);
			if (!updateQuantity.success) {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', updateQuantity?.REQUEST_ID);
				getCurrentScope().setTransactionName('updateConfigQuantity');
				setContext('Error', {
					errorData: JSON.stringify(updateQuantity?.data) || 'Unknown Error Data',
					responseURL: updateQuantity?.request?.responseURL,
					configData: updateQuantity?.config?.data,
				});
				captureMessage(updateQuantity?.data?.error?.message || 'Unknown Error Details');
			}

			const itemAddedToCart = await addItemToCart(props.configKey, props.configurationURL, null, bundleID);
			const dataLayerItem = {};
			props.currentItem?.current_step_data?.questions?.forEach((question) => {
				question.answers.length && (dataLayerItem[question.question_text.charAt(0).toLowerCase() + question.question_text.split('/')[0].slice(1).replace(/\s+/g, '')] = question?.answers?.find(({ id }) => id === question?.selected_answer_id)?.text);
			});
			dataLayerItem['id'] = props.currentItem.mpn || props.backendName;
			dataLayerItem['currency'] = 'USD';
			dataLayerItem['price'] = (props.currentItem.price.amount / 100).toFixed(2);
			dataLayerItem['quantity'] = quantity;
			dataLayerItem['name'] = props.backendName;
			dataLayerItem['category'] = props.productType;

			if (itemAddedToCart?.Cart?.id) {
				if (getCookie('cartadd') && ['window', 'patio-door', 'entry-door'].includes(props.productType)) {
					const expire = new Date();
					setCookie('cartadd', 'configure true cartadd true', new Date(expire.setDate(expire.getDate() + 60)), true);
				}

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'addToCart',
					ecommerce: {
						add: {
							products: [dataLayerItem],
						},
					},
				});
				navigate('/shop/cart/');
			} else {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', itemAddedToCart?.REQUEST_ID);
				getCurrentScope().setTransactionName('addItemToCart');
				setContext('Error', {
					errorData: JSON.stringify(itemAddedToCart?.data) || 'Unknown Error Data',
					responseURL: itemAddedToCart?.request?.responseURL,
					configData: itemAddedToCart?.config?.data,
					item: dataLayerItem,
				});
				captureMessage(itemAddedToCart?.error?.message || 'Unknown Error Details');

				props.updateLoadingStatus(false);
				props.updateErrorMessaging(itemAddedToCart?.error);
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'badConfiguration',
					ecommerce: {
						product: [dataLayerItem],
					},
				});
			}
		} catch (error) {
			props.updateLoadingStatus(false);
			props.updateErrorMessaging(error);
		}
	};

	const updateProject = async () => {
		props.updateLoadingStatus(true);
		try {
			const updateQuantity = await updateConfigQuantity(quantity, props.configKey);
			if (!updateQuantity?.data?.success) {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', updateQuantity?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('updateConfigQuantity');
				setContext('Error', {
					errorData: JSON.stringify(updateQuantity?.data) || 'Unknown Error Data',
					responseURL: updateQuantity?.request?.responseURL,
					configData: updateQuantity?.config?.data,
				});
				captureMessage(updateQuantity?.data?.error?.message || 'Unknown Error Details');
			}

			const updatedProjectItem = await updateProjectItem(editItem.projectID, editItem.groupID, editItem.itemID, { quantity: quantity, config_id: props.configKey, item_url: props.configurationURL });
			if (updatedProjectItem?.data?.id) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'projectUpdate',
				});
				removeSessionStorage('userModified');
				navigate(`/account/project/details/?projectID=${editItem.projectID}`);
			} else {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', updatedProjectItem?.REQUEST_ID);
				getCurrentScope().setTransactionName('updateProjectItem');
				setContext('Error', {
					errorData: JSON.stringify(updatedProjectItem?.data) || 'Unknown Error Data',
					responseURL: updatedProjectItem?.request?.responseURL,
					configData: updatedProjectItem?.config?.data,
				});
				captureMessage(updatedProjectItem?.data?.error?.message || 'Unknown Error Details');

				props.updateLoadingStatus(false);
				props.updateErrorMessaging(updatedProjectItem?.data?.error);
			}
		} catch (error) {
			props.updateLoadingStatus(false);
			props.updateErrorMessaging(error);
		}
	};

	const saveUpdatedProductToCart = async (itemID, quantity) => {
		props.updateLoadingStatus(true);
		try {
			const updateQuantity = await updateConfigQuantity(quantity, props.configKey);

			if (!updateQuantity.success) {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', updateQuantity?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('updateConfigQuantity');
				setContext('Error', {
					errorData: JSON.stringify(updateQuantity?.data) || 'Unknown Error Data',
					responseURL: updateQuantity?.request?.responseURL,
					configData: updateQuantity?.config?.data,
				});
				captureMessage(updateQuantity?.data?.error?.message || 'Unknown Error Details');
			}

			const updatedItem = await updateCartItem(itemID, quantity, props.configKey, props.configurationURL);
			if (updatedItem?.Cart) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'cartUpdate',
				});
				removeSessionStorage('userModified');
				navigate('/shop/cart/');
			} else {
				setTag('Pella Component', 'AddToCart', 'API Transaction ID', updatedItem?.REQUEST_ID);
				getCurrentScope().setTransactionName('updateCartItem');
				setContext('Error', {
					errorData: JSON.stringify(updatedItem) || 'Unknown Error Data',
					responseURL: updatedItem?.request?.responseURL,
					configData: updatedItem?.config?.data,
				});
				captureMessage(updatedItem?.error?.message || 'Unknown Error Details');

				props.updateLoadingStatus(false);
				props.updateErrorMessaging(updatedItem?.error);
			}
		} catch (error) {
			props.updateLoadingStatus(false);
			props.updateErrorMessaging(error);
		}
	};

	const saveBundleToCart = async (id, qty) => {
		props.updateLoadingStatus(true);
		try {
			const bundle = await addBundleToCart(id, qty);

			if (bundle?.Cart?.id && bundle.Cart.Items?.[0]) {
				const dataLayerItem = bundle.Cart.Items.map((item) => ({
					id: item.mpn || item.backend_name,
					currency: item.unit_price.currency,
					price: (item.unit_price?.amount / 100).toFixed(2),
					quantity: item.quantity,
					name: item.backend_name,
					category: item.product_type,
				}));

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'addToCart',
					ecommerce: {
						add: {
							products: dataLayerItem,
						},
					},
				});

				if (!props.showInstallKit) {
					navigate('/shop/cart/');
				}
			} else {
				if (bundle?.error) {
					setTag('Pella Component', 'AddToCart', 'API Transaction ID', bundle?.REQUEST_ID);
					getCurrentScope().setTransactionName('addBundleToCart');
					setContext('Error', {
						errorData: JSON.stringify(bundle) || 'Unknown Error Data',
						responseURL: bundle?.request?.responseURL,
						configData: bundle?.config?.data,
					});
					captureMessage(bundle?.error?.message || 'Unknown Error Details');

					props.updateErrorMessaging(bundle.error);
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						event: 'badConfiguration',
						ecommerce: {
							product: [{}],
						},
					});
				}
				props.updateLoadingStatus(false);
			}
		} catch (error) {
			props.updateLoadingStatus(false);
			props.updateErrorMessaging(error);
		}
	};

	const onInstallCheck = (event) => setAddInstallKit(event.target.checked);

	const bundlePrice = `Add (${quantity}) Installation Kits - ${props.isPro ? `<span>$${((bundleData?.price?.professional?.amount * parseInt(quantity)) / 100).toFixed(2)}</span>` : ''}<span>$${((bundleData?.price?.consumer?.amount * parseInt(quantity)) / 100).toFixed(2)}</span>`;

	return (
		<>
			{props.showInstallKit && !editItem && (
				<div className={`${styles.addToCart_Install} mt-4 bg-neutral-50 p-3 md:mt-5`}>
					<p className='mb-1 font-semibold'>{CSData?.summary?.installation_kit}</p>
					<Checkbox
						id='installKit'
						onChange={onInstallCheck}
						labelText={
							<div
								className={styles.addToCart_InstallLabel}
								dangerouslySetInnerHTML={{ __html: bundlePrice }}
							/>
						}
					/>
				</div>
			)}

			<div className={`${styles.addToCart} ${props.className ? props.className : ''} ${props.inventoryError ? styles.inventoryError : ''}`}>
				{!props.hideQty && (
					<Incrementer
						value={quantity}
						change={updateQuantity}
					/>
				)}

				<Button
					type='button'
					color='primary'
					onClick={editItem?.src === 'project' ? updateProject : saveToCart}
					disabled={props.limitations !== null}
					className={styles.addToCart_button}
					id={'speedcurve-add-to-cart'}
				>
					{editItem ? (
						'save build'
					) : (
						<>
							<Icon name='shopping_cart' /> add to cart
						</>
					)}
				</Button>
			</div>
		</>
	);
};

AddToCart.propTypes = {
	currentItem: PropTypes.object,
	updateQuantity: PropTypes.func,
	userModification: PropTypes.bool,
	detailsHeading: PropTypes.string,
	configKey: PropTypes.string,
	updateLoadingStatus: PropTypes.func,
	updateErrorMessaging: PropTypes.func,
	limitations: PropTypes.array,
	addToProjectCTA: PropTypes.object,
	editItem: PropTypes.object,
	backendName: PropTypes.string,
	productType: PropTypes.string,
	className: PropTypes.string,
	fullName: PropTypes.string,
	configurationURL: PropTypes.string,
	hideQty: PropTypes.bool,
	bundleID: PropTypes.string,
	isPro: PropTypes.bool,
	inventoryError: PropTypes.bool,
	itemEdited: PropTypes.bool,
	itemQty: PropTypes.number,
	showInstallKit: PropTypes.bool,
	isBundlePDP: PropTypes.bool,
};

export default AddToCart;

export const query = graphql`
	{
		contentstackSummaryFields {
			summary {
				installation_kit
			}
		}
	}
`;
